import type { RequestHandler } from "express";

/**
 * Get handler of an ApiClient
 */
type GetHandler<C extends ApiClient> = C extends ApiClient<infer T> ? T : unknown;

/**
 * Get route params from an ApiClient
 */
type GetRouteParams<C extends ApiClient> =
    C extends ApiClient<
        // We need to ignore first param to return the 2nd
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        infer T,
        infer P
    >
        ? P
        : unknown;

/**
 * Get response type from RequestHandler
 */
type GetBodyRes<C extends RequestHandler> =
    C extends RequestHandler<
        // We need to ignore first param to return the 2nd
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        infer T,
        infer ResBody
    >
        ? ResBody
        : unknown;

/**
 * Get body type from RequestHandler
 */
type GetBodyReq<C extends RequestHandler> =
    C extends RequestHandler<
        // We need to ignore first params to return the 3nd
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        infer T,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        infer ResBody,
        infer ReqBody
    >
        ? ReqBody
        : unknown;

/**
 * Api client to be used in frontend. This can be passed to getFetcher
 *
 * @example
 * const fetcher = getFetcher(apiClient, commonSettings);
 * const result = await fetcher(body);
 */
export type ApiClient<
    // We use "Handler" in types
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Handler extends RequestHandler = RequestHandler,
    Params extends Record<string, any> = any,
> = {
    method: "get" | "post";
    route: (params: Params) => string;
};

/**
 * Will create a fetcher function to start fetching data.
 *
 * @param endpoint Endpoint to use
 * @param params Params to be used in the url of the api
 * @returns Function to be called to execute fetch of an api
 */
export const getFetcher = <T extends ApiClient>(endpoint: T, params: GetRouteParams<T>) => {
    return (body?: GetBodyReq<GetHandler<T>>, init?: RequestInit): Promise<GetBodyRes<GetHandler<T>>> =>
        new Promise((resolve, reject) => {
            fetch(endpoint.route(params), {
                method: endpoint.method.toUpperCase(),
                headers: { "Content-Type": "application/json" },
                ...(init ?? {}),
                ...(body ? { body: JSON.stringify(body) } : {}),
            })
                .then((res) => res.json())
                .then((json) => resolve(json))
                .catch(reject);
        });
};
